import React, { useEffect, useState } from "react";

import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { v4 as uuidv4 } from "uuid";

import { mermaidConfig } from "src/base-components/MermaidDiagram/mermaidConfig";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { EmptyState } from "src/design-system/EmptyState";

type MermaidDiagramProps = {
  chart: string;
  dataLoc?: string;
};

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({
  chart,
  dataLoc,
}) => {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const renderChart = async () => {
      const id = `mermaid-${uuidv4()}`;

      try {
        const mermaid = (await import("mermaid")).default;
        mermaid.initialize(mermaidConfig);

        const parseResult = await mermaid.parse(chart, {
          suppressErrors: true,
        });

        if (!parseResult) {
          if (!cancelled) {
            setError("Invalid diagram syntax");
            setSvg(null);
          }
          return;
        }

        const { svg: renderedSvg } = await mermaid.render(id, chart);

        if (!cancelled) {
          setSvg(renderedSvg);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to render diagram",
          );
          setSvg(null);
        }
      }
    };

    renderChart();

    return () => {
      cancelled = true;
    };
  }, [chart]);

  if (error) {
    return (
      <div className="rounded-lg border border-gray-100">
        <EmptyState
          dataLoc={dataLoc ? `${dataLoc}-mermaid-error` : undefined}
          description={error}
          headline="Mermaid diagram error"
          icon={faExclamationCircle}
          variant="error"
        />
      </div>
    );
  }

  if (!svg) {
    return (
      <SkeletonPlaceholder
        dataLoc={dataLoc ? `${dataLoc}-mermaid-loading` : undefined}
        height="h-48"
        rounded="rounded-lg"
      />
    );
  }

  return (
    <div
      // mermaid config uses strict security level so we can  use dangerouslySetInnerHTML
      //skipcq: JS-0440
      dangerouslySetInnerHTML={{ __html: svg }}
      data-loc={dataLoc ? `${dataLoc}-mermaid` : undefined}
    />
  );
};
